import { call, put, select } from 'redux-saga/effects'
import encodeBazaarVoiceContent from '../../helpers/encodeBazaarVoiceContent'
import formatTimestampToDateHelper from '../../helpers/formatTimestampToDate'
import Logger from '../../helpers/Logger'
import { fetchApi } from '../helpers/fetchApi'

import {
  fetchBVReviewFailure,
  fetchBVReviewSuccess
} from '../../actionCreators/bazaarVoiceReview/bazaarVoiceReviewActionCreator'
import { isValidSku } from '../../helpers/skuValidator'

const logger = Logger.getInstance()
const endpoint = '/product-api/bazaar-voice-reviews/partNumber'

const generateStructuredStats = (partNumber, data) => {
  const { Includes, TotalResults } = data
  logger.debug('functionLogger', { arguments: data }, 'BazaarVoiceReviewStore generateStructuredStats')

  if (Includes.Products) {
    const SKUS = Object.keys(Includes.Products)

    const partNumberWhichHasSecondaryRatings = SKUS.filter(
      prtn => Includes.Products[prtn].ReviewStatistics.SecondaryRatingsAveragesOrder.length > 0
    )[0]
    const statsPartNumber = partNumberWhichHasSecondaryRatings || SKUS[0]
    const stats = Includes.Products[statsPartNumber].ReviewStatistics

    const secondaryRatings = Object.keys(stats.SecondaryRatingsAverages).reduce((obj, ratingKey) => {
      obj[stats.SecondaryRatingsAverages[ratingKey].Label] = stats.SecondaryRatingsAverages[ratingKey].AverageRating // eslint-disable-line
      return obj
    }, {})

    const recommendationPercentage =
      Math.round((stats.RecommendedCount / (stats.RecommendedCount + stats.NotRecommendedCount)) * 100) || 0
    return {
      attributes: {
        overallRating: stats.AverageOverallRating,
        reviewCount: TotalResults,
        secondaryRatings,
        recommendationPercentage,
        ratingDistribution: stats.RatingDistribution
      }
    }
  }
  return { attributes: {} }
}

const generateStructuredReview = (...args) => {
  const [review, fullResponse] = args
  logger.debug('functionLogger', { arguments: args }, 'BazaarVoiceReviewStore generateStructuredReview')

  const secondaryRatings = review.SecondaryRatings
  const contextDataValues = review.ContextDataValues
  const syndication = review.SyndicationSource
  const productIncludes = fullResponse.Includes.Products
  const { Name: productName, Id: productId } = productIncludes[review.ProductId]

  return {
    id: parseInt(review.Id, 10),
    type: 'review',
    attributes: {
      submitted: formatTimestampToDateHelper(review.SubmissionTime),
      overallRating: review.Rating,
      secondaryRatings: Object.keys(secondaryRatings).reduce((obj, ratingKey) => {
        obj[secondaryRatings[ratingKey].Label] = secondaryRatings[ratingKey].Value // eslint-disable-line
        return obj
      }, {}),
      title: review.Title,
      text: encodeBazaarVoiceContent(review.ReviewText),
      recommended: review.IsRecommended,
      profile: {
        name: review.UserNickname,
        location: review.UserLocation,
        contextAttributes: Object.keys(review.ContextDataValues).reduce((obj, contextKey) => {
          obj[contextDataValues[contextKey].DimensionLabel] = contextDataValues[contextKey].ValueLabel // eslint-disable-line
          return obj
        }, {}),
        badges: Object.keys(review.Badges).map(badge => review.Badges[badge].Id)
      },
      helpfulness: {
        helpful: review.TotalPositiveFeedbackCount,
        unhelpful: review.TotalNegativeFeedbackCount
      },
      photos: review.Photos,
      productReviewed: {
        name: productName,
        id: productId
      },
      recommendedProducts: review.ProductRecommendationIds.map(id => ({
        id,
        name: productIncludes[id].Name,
        rating: productIncludes[id].ReviewStatistics.AverageOverallRating
      })),
      isSyndicated: review.IsSyndicated,
      syndication: syndication
        ? {
            img: syndication.LogoImageUrl,
            name: syndication.Name,
            url: syndication.ContentLink
          }
        : null,
      totalClientResponseCount: review.TotalClientResponseCount,
      clientResponses: review.ClientResponses
    }
  }
}

const generateSuccessAction = (
  partNumber,
  oldData,
  stats,
  cache,
  allFetchedReviews,
  sortOrder,
  starFilter,
  reviewsSummary
) => {
  const productReview = oldData.productReviews ? oldData.productReviews[partNumber] : undefined
  const reviews = productReview || allFetchedReviews

  const newData = {
    reviews,
    stats,
    cache,
    allFetchedReviews,
    sortOrder,
    starFilter,
    reviewsSummary
  }

  return fetchBVReviewSuccess({ partNumber, newData })
}

const bazaarVoiceReview = state => state.bazaarVoiceReview

function* bazaarVoiceReviewSaga(action, checkCache = true, baseUrl, cookies) {
  const { payload } = action
  const { partNumber, query } = payload
  const url = `${baseUrl || ''}${endpoint}/${partNumber}`
  const sortOrder = query.Sort
  const starFilter = query.filter

  try {
    if (!isValidSku(partNumber)) {
      const error = new Error('Invalid part number')
      error.status = 400
      throw error
    }
    const { data, meta } = yield select(bazaarVoiceReview)
    const cacheId = `${payload.partNumber}_${query.Limit}_${query.Offset}_${query.Sort}_${query.filter}`
    // Case 1: Check cache to see if you already have data for part number
    if (checkCache) {
      const { cache } = data
      const isInCache = cache[cacheId] && !meta[partNumber].errorDetail
      if (isInCache) {
        const stats = generateStructuredStats(partNumber, cache[cacheId].data)
        const allFetchedReviews = cache[cacheId].data.Results.map(review =>
          generateStructuredReview(review, cache[cacheId].data)
        )

        const successAction = generateSuccessAction(
          partNumber,
          data,
          stats,
          null,
          allFetchedReviews,
          sortOrder,
          starFilter,
          cache[cacheId].reviewsSummary
        )
        return yield put(successAction)
      }
    }

    // Case 2: Make Ajax call to get data for part number
    let headers = {}
    if (cookies) {
      headers = {
        Cookie: cookies,
        ...headers
      }
    }
    const jsonData = yield call(fetchApi, url, { ...query, returnMeta: true }, headers)

    const stats = generateStructuredStats(partNumber, jsonData.data)
    const allFetchedReviews = jsonData.data.Results.map(review => generateStructuredReview(review, jsonData.data))
    const cache = {
      [cacheId]: jsonData
    }
    const successAction = generateSuccessAction(
      partNumber,
      data,
      stats,
      cache,
      allFetchedReviews,
      sortOrder,
      starFilter,
      jsonData.reviewsSummary
    )
    return yield put(successAction)
  } catch (error) {
    const data = {
      partNumber,
      errorDetail: error.toString()
    }
    if (error.response && error.response.body && error.response.body.errors) {
      data.errorDetail = error.response.body.errors
    }
    const failureAction = fetchBVReviewFailure(data)
    return yield put(failureAction)
  }
}
export { endpoint, bazaarVoiceReviewSaga, bazaarVoiceReview }
